import type { z } from "zod";

import type { AnyMenuItem, MenuItem, MenuItemLink } from "./menu";
import { INVALID_MENU_URL_MESSAGE, isNavigableMenuUrl } from "./menu-url";

const collectLinks = (
  items: readonly AnyMenuItem[],
  into: MenuItemLink[],
): void => {
  for (const item of items) {
    if (!("kind" in item)) into.push(item);
    if ("children" in item) collectLinks(item.children, into);
  }
};

/**
 * `pageId` belongs to page links only, and `followTitle` to links that have a
 * `pageId` (feature: page-addresses). A page id on a tour link would make the
 * served menu resolve a page's address over the tour's, so it is refused on
 * save rather than ignored.
 *
 * Its own file so `menu.ts` stays under the line cap. Type-only imports from
 * there, so the two files do not load each other at runtime.
 */
export function pageLinkFields(
  items: readonly MenuItem[],
  ctx: z.RefinementCtx,
): void {
  const links: MenuItemLink[] = [];
  collectLinks(items, links);
  for (const link of links) {
    if (link.pageId !== undefined && link.source !== "page") {
      ctx.addIssue({
        code: "custom",
        message: `Menu item "${link.label}" names a page but is not a page link.`,
      });
    }
    if (link.followTitle !== undefined && link.pageId === undefined) {
      ctx.addIssue({
        code: "custom",
        message: `Menu item "${link.label}" follows a page title but names no page.`,
      });
    }
  }
}

/**
 * Every link must reach a real place (TC-65 #10). On the save schema only, not
 * on `menuItemsReadSchema`: menus saved before this rule can hold a link like
 * `www.example.com`, and one such link must not cost the agency its whole
 * stored menu on the storefront.
 */
export function navigableLinkUrls(
  items: readonly MenuItem[],
  ctx: z.RefinementCtx,
): void {
  const links: MenuItemLink[] = [];
  collectLinks(items, links);
  for (const link of links) {
    if (!isNavigableMenuUrl(link.url)) {
      ctx.addIssue({
        code: "custom",
        message: `Menu item "${link.label}": ${INVALID_MENU_URL_MESSAGE}`,
      });
    }
  }
}
